// The composer's attachments: the paperclip (first in the toolbar), paste, drag & drop onto the
// whole pane, and the tray above the text box — the Mac's staging chips
// (MacConversationView.MacStagedAttachmentsRow, its staging-error line, its "Suelta para adjuntar"
// drop overlay) plus what an upload-while-staged web tray needs: per-file progress, a failed state
// with retry. Composer wires it with a handful of lines; everything else lives here.
import { useContext, useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AttachmentKind } from '../../../core/attachmentKind';
import { fileBadge, pasteWantsFiles, progressFraction, sendGate, type SendGate, MAX_FILES_PER_POST } from '../../../core/attachmentStaging';
import { Icon } from '../../../design/icons';
import { locale, t, tf } from '../../../i18n/i18n';
import { ShellContext } from '../../shell/ShellContext';
import { addFiles, currentLimits, dismissNotice, loadLimits, removeItem, restoreTray, retryItem, takeForSend, trayOf, useTray, type Incoming, type TrayItem, type TrayNotice } from './tray';
import './attachments.css';

export interface ComposerAttachments {
  /** Notice + tray, above the text box (render inside `.composer`). */
  tray: ReactNode;
  /** The paperclip (+ its hidden picker), first in the toolbar. */
  button: ReactNode;
  onPaste(e: ClipboardEvent<HTMLTextAreaElement>): void;
  gate(text: string): SendGate;
  /** Anything in the tray (a command cannot take a file: with files, "/x" is a message). */
  hasFiles(): boolean;
  /** The send's file ids; empties the tray. */
  take(): string[];
  /** The same ids WITHOUT emptying the tray (a schedule clears it only once the server agreed). */
  peek(): string[];
  /** A hint for the send button's tooltip ("se enviará al terminar de subir"), or null. */
  sendHint(gate: SendGate): string | null;
}

// A file dropped OUTSIDE a conversation must not navigate the tab away to the file (the browser's
// default for a drop nobody handled): refused once, for the whole window.
let windowGuard = false;
function guardWindow(): void {
  if (windowGuard || typeof window === 'undefined') return;
  windowGuard = true;
  const isFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');
  window.addEventListener('dragover', (e) => isFiles(e) && e.preventDefault());
  window.addEventListener('drop', (e) => isFiles(e) && e.preventDefault());
}

/** Files out of a drop, folders flagged (they cannot be uploaded and are refused by name). */
function dropped(dt: DataTransfer): Incoming[] {
  const out: Incoming[] = [];
  const items = Array.from(dt.items ?? []);
  if (items.length) {
    for (const item of items) {
      if (item.kind !== 'file') continue;
      const file = item.getAsFile();
      if (!file) continue;
      const entry = (item as DataTransferItem & { webkitGetAsEntry?: () => { isDirectory: boolean } | null }).webkitGetAsEntry?.();
      out.push({ file, source: 'drop', isDirectory: !!entry?.isDirectory });
    }
    if (out.length) return out;
  }
  return Array.from(dt.files ?? []).map((file) => ({ file, source: 'drop' as const }));
}

export function useComposerAttachments(opts: { channelKey: string; channelId: string; anchor: RefObject<HTMLDivElement | null>; disabled: boolean }): ComposerAttachments {
  const { channelKey, channelId, anchor, disabled } = opts;
  const tray = useTray(channelKey);
  const [enabled, setEnabled] = useState(currentLimits()?.enabled ?? true);
  const [pane, setPane] = useState<HTMLElement | null>(null);
  const [dropping, setDropping] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    void loadLimits().then((l) => alive && setEnabled(l.enabled));
    return () => {
      alive = false;
    };
  }, []);

  // A tray this browser held before a reload comes back (P096): bytes from the worker's vault.
  const shell = useContext(ShellContext);
  const meId = shell?.me.id ?? '';
  useEffect(() => {
    if (!meId) return;
    void restoreTray(channelKey, meId).then((back) => back && shell?.toast(t('web.attachments.restored')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelKey, meId]);

  // The pane the composer floats in is the drop target (the Mac's whole-conversation drop).
  useLayoutEffect(() => {
    setPane(anchor.current?.parentElement ?? null);
  });

  useEffect(() => {
    if (!pane || disabled || !enabled) return;
    guardWindow();
    let depth = 0;
    const isFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');
    const enter = (e: DragEvent) => {
      if (!isFiles(e)) return;
      e.preventDefault();
      depth += 1;
      setDropping(true);
    };
    const over = (e: DragEvent) => {
      if (!isFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const leave = (e: DragEvent) => {
      if (!isFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDropping(false);
    };
    const drop = (e: DragEvent) => {
      if (!isFiles(e) || !e.dataTransfer) return;
      e.preventDefault();
      depth = 0;
      setDropping(false);
      void addFiles(channelKey, channelId, dropped(e.dataTransfer));
    };
    pane.addEventListener('dragenter', enter);
    pane.addEventListener('dragover', over);
    pane.addEventListener('dragleave', leave);
    pane.addEventListener('drop', drop);
    return () => {
      pane.removeEventListener('dragenter', enter);
      pane.removeEventListener('dragover', over);
      pane.removeEventListener('dragleave', leave);
      pane.removeEventListener('drop', drop);
    };
  }, [pane, channelKey, channelId, disabled, enabled]);

  const states = tray.items.map((i) => i.state);

  const button = enabled ? (
    <>
      <button
        className="icon-button attach-button"
        title={t('mac.attachFile')}
        aria-label={t('mac.attachFile')}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => input.current?.click()}
      >
        <Icon name="paperclip" size={16} />
      </button>
      <input
        ref={input}
        className="attach-input"
        type="file"
        multiple
        hidden
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          void addFiles(channelKey, channelId, files.map((file) => ({ file, source: 'picker' as const })));
        }}
      />
    </>
  ) : null;

  const trayNode = (
    <>
      {tray.notice ? <Notice notice={tray.notice} onDismiss={() => dismissNotice(channelKey)} /> : null}
      {tray.items.length ? (
        <div className="attach-tray" role="list" aria-label={t('mac.attachFile')}>
          {tray.items.map((item) => (
            <TrayCard key={item.localId} item={item} onRemove={() => removeItem(channelKey, item.localId)} onRetry={() => retryItem(channelKey, item.localId)} />
          ))}
        </div>
      ) : null}
      {states.includes('failed') ? <div className="attach-hint">{t('web.attach.blockedHint')}</div> : null}
      {dropping && pane
        ? createPortal(
            <div className="attach-drop-overlay" aria-hidden>
              <span className="attach-drop-label">
                <Icon name="paperclip" size={16} />
                {t('channel.dropToAttach')}
              </span>
            </div>,
            pane,
          )
        : null}
    </>
  );

  return {
    tray: trayNode,
    button,
    onPaste(e) {
      if (disabled || !enabled) return;
      const files = Array.from(e.clipboardData?.files ?? []);
      const plain = e.clipboardData?.getData('text/plain') ?? '';
      if (!pasteWantsFiles(plain, files.map((f) => ({ name: f.name, mimeType: f.type })))) return;
      e.preventDefault();
      void addFiles(channelKey, channelId, files.map((file) => ({ file, source: 'paste' as const })));
    },
    gate: (text) => sendGate(text, trayOf(channelKey).items.map((i) => i.state)),
    hasFiles: () => trayOf(channelKey).items.length > 0,
    take: () => takeForSend(channelKey),
    peek: () => trayOf(channelKey).items.map((i) => i.fileId ?? i.localId),
    sendHint: (gate) => (gate === 'queued' ? t('web.attach.queuedHint') : gate === 'blocked' ? t('web.attach.blockedHint') : null),
  };
}

function Notice({ notice, onDismiss }: { notice: TrayNotice; onDismiss(): void }) {
  const limits = currentLimits();
  let text: string;
  switch (notice.reason) {
    case 'tooMany':
      text = tf('web.attach.tooMany', limits?.maxFiles ?? MAX_FILES_PER_POST);
      break;
    case 'tooLarge':
      text = tf('web.attach.tooLarge', notice.name, AttachmentKind.sizeLabel(limits?.maxFileSize ?? 50 * 1024 * 1024));
      break;
    case 'unreadable':
      text = tf('web.attach.unreadable', notice.name);
      break;
    default:
      text = t('web.attach.disabled');
  }
  if (notice.others > 0 && notice.reason !== 'tooMany') text = tf('web.attach.andOthers', text, notice.others);
  return (
    <div className="attach-notice" role="alert">
      <Icon name="alertCircle" size={14} />
      <span>{text}</span>
      <button className="attach-notice-close" onClick={onDismiss} title={t('common.close')} aria-label={t('common.close')}>
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}

const percent = () => new Intl.NumberFormat(locale(), { style: 'percent', maximumFractionDigits: 0 });

function TrayCard({ item, onRemove, onRetry }: { item: TrayItem; onRemove(): void; onRetry(): void }) {
  const fraction = progressFraction(item.loaded, item.total);
  const caption =
    item.state === 'failed' ? t('web.attach.failed') : item.state === 'uploading' ? percent().format(fraction) : AttachmentKind.sizeLabel(item.size);
  const isImage = item.kind === 'image' && item.objectUrl;
  return (
    <div className={`attach-card${isImage ? ' is-image' : ''}`} role="listitem" data-state={item.state} title={item.error ? `${item.name} — ${item.error}` : item.name}>
      {isImage ? (
        <img className="attach-thumb" src={item.objectUrl!} alt={item.name} draggable={false} />
      ) : (
        <>
          <span className="attach-badge">{fileBadge(item.name)}</span>
          <span className="attach-text">
            <span className="attach-name">{item.name}</span>
            <span className="attach-meta">{caption}</span>
          </span>
        </>
      )}
      {isImage && item.state !== 'done' ? <span className="attach-thumb-caption">{caption}</span> : null}
      {item.state === 'uploading' ? (
        <span
          className="attach-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(fraction * 100)}
          aria-label={tf('web.attach.uploading', item.name)}
        >
          <i style={{ width: `${Math.max(4, fraction * 100)}%` }} />
        </span>
      ) : null}
      {item.state === 'failed' ? (
        <button className="attach-retry" onClick={onRetry} title={t('common.retry')} aria-label={t('common.retry')}>
          <Icon name="retry" size={14} />
        </button>
      ) : null}
      <button className="attach-remove" onClick={onRemove} title={tf('web.attach.remove', item.name)} aria-label={tf('web.attach.remove', item.name)}>
        <Icon name="x" size={11} />
      </button>
    </div>
  );
}
